import { Link } from "react-router-dom";
import {
  HardHat, ArrowRight, ShieldCheck, FileText, Smartphone, Building2, Clock, CheckCircle2,
} from "lucide-react";

const STEPS = [
  { icon: FileText, t: "You apply", d: "Company name, one office contact, and the trades you cover. Two minutes." },
  { icon: ShieldCheck, t: "You finish setup", d: "Your crew list, your insurance certificate, your W-9, and the vendor agreement — all from a link on your phone." },
  { icon: Clock, t: "We review", d: "We check the certificate is current, the W-9 is on file and the agreement is signed. You get a text either way." },
  { icon: Building2, t: "Communities add you", d: "Once you're in the network, any community we manage can switch you on for their property. You never apply twice." },
];

const TRUTHS = [
  { icon: Smartphone, t: "No app, no password", d: "A job arrives as a text with a link. Tap it, see the work, the address, the photos and who to call. Your crew does the same on site." },
  { icon: ShieldCheck, t: "One certificate, every property", d: "Your insurance lives on your company record. If it lapses, new jobs pause until you send the new one — so you never get sent work you're not covered for." },
  { icon: Building2, t: "Your crew stays yours", d: "You name who's on site at each property and change it any time. We never message a crew member who hasn't confirmed their own phone." },
  { icon: FileText, t: "Paper trail both ways", d: "Time on site, photos and notes are stamped to the job — so a dispute is settled by the record, not by memory." },
];

export default function VendorNetwork() {
  return (
    <div className="min-h-screen bg-sand-50" data-testid="vendor-network-page">
      <div className="border-b border-sand-200 bg-white">
        <div className="mx-auto max-w-3xl px-5 py-14">
          <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-redline-600">
            <HardHat className="h-3.5 w-3.5" /> Ask Redline vendor network
          </div>
          <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl">
            Get the work.<br />Skip the phone tag.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-ink/60">
            Ask Redline runs the front desk for HOA and condo communities. When something breaks, the
            request becomes a work order — and that work order goes to a vendor in this network. One
            setup, one certificate on file, and every community we manage can send you work.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/vendor-apply" data-testid="vn-apply-top"
              className="inline-flex items-center gap-2 rounded-xl bg-redline-500 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-redline-600">
              Apply to join <ArrowRight className="h-4 w-4" />
            </Link>
            <span className="text-xs text-ink/45">Free. No listing fee, no lead fee.</span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-5 py-14">
        <h2 className="font-heading text-lg font-extrabold text-ink md:text-lg">How joining works</h2>
        <div className="mt-6 space-y-3">
          {STEPS.map((s, i) => (
            <div key={s.t} data-testid={`vn-step-${i + 1}`}
              className="flex gap-4 rounded-2xl border border-sand-200 bg-white p-5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ink/5">
                <s.icon className="h-5 w-5 text-ink/70" strokeWidth={1.6} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-redline-600">STEP {i + 1}</span>
                  <span className="font-heading text-base font-bold text-ink">{s.t}</span>
                </div>
                <p className="mt-1 text-sm leading-relaxed text-ink/60">{s.d}</p>
              </div>
            </div>
          ))}
        </div>

        <h2 className="mt-14 font-heading text-lg font-extrabold text-ink md:text-lg">What being in the network means</h2>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {TRUTHS.map((t) => (
            <div key={t.t} data-testid={`vn-truth-${t.t.split(" ")[0].toLowerCase()}`}
              className="rounded-2xl border border-sand-200 bg-white p-5">
              <t.icon className="h-5 w-5 text-redline-500" strokeWidth={1.6} />
              <div className="mt-3 font-heading text-base font-bold text-ink">{t.t}</div>
              <p className="mt-1 text-sm leading-relaxed text-ink/60">{t.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-2xl bg-ink p-7 text-sand-200">
          <h2 className="font-heading text-lg font-extrabold text-white md:text-lg">What we need from you</h2>
          <ul className="mt-4 space-y-2.5 text-sm leading-relaxed">
            {[
              "A certificate of insurance with a current expiry date (general liability; workers' comp if you have employees).",
              "A signed W-9 so we can pay you without chasing paperwork later.",
              "The vendor agreement, signed by whoever can sign for the company.",
              "One office contact who can accept a job, and the crew who actually show up.",
            ].map((x) => (
              <li key={x} className="flex gap-2.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                <span>{x}</span>
              </li>
            ))}
          </ul>
          <Link to="/vendor-apply" data-testid="vn-apply-bottom"
            className="mt-7 inline-flex items-center gap-2 rounded-xl bg-redline-500 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-redline-600">
            Start your application <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-4 text-xs text-sand-300/70">
            Already applied? Use the setup link we texted or emailed you — it picks up where you left off.
          </p>
        </div>

        <div className="mt-10 text-center text-xs text-ink/40">
          <Link to="/" className="hover:text-ink/70">Ask Redline</Link> · Austin, TX ·{" "}
          <Link to="/privacy" className="hover:text-ink/70">Privacy</Link> ·{" "}
          <Link to="/terms" className="hover:text-ink/70">Terms</Link>
        </div>
      </div>
    </div>
  );
}
